/** @format */

import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const ACCENT = '#D4A017';

const navBtnSx = {
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 2,
  width: 32,
  height: 32,
  color: 'rgba(255,255,255,0.85)',
  bgcolor: 'rgba(255,255,255,0.08)',
  border: '1px solid rgba(255,255,255,0.12)',
  backdropFilter: 'blur(10px)',
  '&:hover': {
    bgcolor: 'rgba(255,255,255,0.16)',
    color: '#fff',
  },
};

export default function WorkroomCarousel({
  open,
  images = [],
  initialIndex = 0,
  onClose,
}) {
  const [index, setIndex] = useState(initialIndex);
  const [loadedUrl, setLoadedUrl] = useState(null);
  const count = images.length;

  useEffect(() => {
    if (open) {
      setIndex(Math.min(Math.max(0, initialIndex), Math.max(0, count - 1)));
    }
  }, [open, initialIndex, count]);

  const goPrev = useCallback(() => {
    if (count < 2) return;
    setIndex((current) => (current - 1 + count) % count);
  }, [count]);

  const goNext = useCallback(() => {
    if (count < 2) return;
    setIndex((current) => (current + 1) % count);
  }, [count]);

  useEffect(() => {
    if (!open || count < 2) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goNext();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, count, goPrev, goNext]);

  const active = images[index] ?? null;
  const imageLoading = Boolean(active?.url) && loadedUrl !== active.url;

  const handleImageReady = useCallback((url) => {
    setLoadedUrl(url);
  }, []);


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          m: 1.5,
          width: { xs: 'calc(100vw - 24px)', sm: 560 },
          maxWidth: '92vw',
          bgcolor: 'transparent',
          boxShadow: 'none',
          overflow: 'visible',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(18, 14, 10, 0.55)',
            backdropFilter: 'blur(10px)',
          },
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          borderRadius: '16px',
          overflow: 'hidden',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          background:
            'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.04))',
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
          color: '#fff',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 1.75, pt: 1.25, pb: 0.75 }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.48)',
            }}
          >
            Reference · {count ? index + 1 : 0}/{count}
          </Typography>
          <IconButton
            aria-label="Close carousel"
            onClick={onClose}
            size="small"
            sx={{
              width: 28,
              height: 28,
              color: 'rgba(255,255,255,0.55)',
              '&:hover': {
                color: '#fff',
                background: 'rgba(255,255,255,0.08)',
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>

        <Box
          sx={{
            position: 'relative',
            mx: 1.5,
            height: { xs: 260, sm: 320 },
            borderRadius: '12px',
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.08)',
            bgcolor: 'rgba(0,0,0,0.28)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {count > 1 ? (
            <IconButton
              aria-label="Previous image"
              onClick={goPrev}
              sx={{ ...navBtnSx, left: 8 }}
            >
              <ChevronLeftIcon sx={{ fontSize: 20 }} />
            </IconButton>
          ) : null}

          {imageLoading ? (
            <CircularProgress
              size={28}
              sx={{
                position: 'absolute',
                color: 'rgba(255,255,255,0.45)',
              }}
            />
          ) : null}

          {active?.url ? (
            <Box
              component="img"
              key={active.url}
              src={active.url}
              alt={active.id || `Reference ${index + 1}`}
              onLoad={() => handleImageReady(active.url)}
              onError={() => handleImageReady(active.url)}
              ref={(img) => {
                if (img?.complete) handleImageReady(active.url);
              }}
              sx={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: '100%',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                opacity: imageLoading ? 0 : 1,
                transition: 'opacity 160ms ease',
              }}
            />
          ) : null}

          {count > 1 ? (
            <IconButton
              aria-label="Next image"
              onClick={goNext}
              sx={{ ...navBtnSx, right: 8 }}
            >
              <ChevronRightIcon sx={{ fontSize: 20 }} />
            </IconButton>
          ) : null}
        </Box>

        {count > 1 ? (
          <Stack
            direction="row"
            spacing={0.75}
            justifyContent="center"
            alignItems="center"
            sx={{ px: 1.5, py: 1.25 }}
          >
            {images.map((image, thumbIndex) => {
              const isActive = thumbIndex === index;
              return (
                <Box
                  key={image.id}
                  component="button"
                  type="button"
                  aria-label={`Go to reference ${thumbIndex + 1}`}
                  aria-current={isActive ? 'true' : undefined}
                  onClick={() => setIndex(thumbIndex)}
                  sx={{
                    width: isActive ? 40 : 34,
                    height: isActive ? 40 : 34,
                    p: 0,
                    borderRadius: '8px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    flexShrink: 0,
                    border: isActive
                      ? `1.5px solid ${ACCENT}`
                      : '1px solid rgba(255,255,255,0.12)',
                    boxShadow: isActive ? `0 0 10px ${ACCENT}33` : 'none',
                    opacity: isActive ? 1 : 0.5,
                    backgroundImage: `url(${image.url})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    transition:
                      'opacity 160ms ease, width 160ms ease, height 160ms ease, border-color 160ms ease',
                    '&:hover': { opacity: 0.9 },
                  }}
                />
              );
            })}
          </Stack>
        ) : (
          <Box sx={{ height: 12 }} />
        )}
      </Box>
    </Dialog>
  );
}

export { ACCENT as WORKROOM_CAROUSEL_ACCENT };
